import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router'
import { Button, Eyebrow, Field, Input, Notice } from '@/ui'
import { useAuth } from './AuthProvider'

export function SignInPage() {
  const { user, loading, error, signInWithGoogle, sendEmailLink } = useAuth()
  const [params] = useSearchParams()
  const loc = useLocation()
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  // Where to land after signing in: the page a guard bounced from, or the page this form is rendered on (an invite link).
  const onSignInRoute = loc.pathname === '/sign-in'
  const next = (loc.state as { from?: string } | null)?.from ?? (onSignInRoute ? '/' : loc.pathname + loc.search)

  if (!loading && user) return <Navigate to={next} replace />

  const onEmail = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setLocalError(null)
    try {
      await sendEmailLink(email.trim(), next)
      setSent(true)
    } catch {
      setLocalError('We could not send a link to that address. Check it and try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main id="main" className="grid min-h-dvh md:grid-cols-2">
      <section className="on-dark hidden bg-eerie px-10 py-16 text-paper md:flex md:flex-col md:justify-between">
        <img src="/brand/sonder-logo.png" alt="Sonder Media House" width={131} height={40} className="invert" />
        <div className="grid gap-4">
          <Eyebrow tone="inverse">Client portal</Eyebrow>
          <h1 className="text-paper">Stories in every frame.</h1>
          <p className="max-w-md text-paper/70">Confirm your details, connect your channels, and share what we need before the first shoot.</p>
        </div>
        <p className="text-small text-paper/50">© Sonder Media House, LLC</p>
      </section>
      <section className="grid place-items-center px-6 py-16">
        <div className="grid w-full max-w-sm gap-8">
          <img src="/brand/sonder-logo.png" alt="Sonder Media House" width={131} height={40} className="md:hidden" />
          <div className="grid gap-2">
            <Eyebrow tone="muted">Sign in</Eyebrow>
            <h2 className="text-h3">Welcome back.</h2>
            <p className="text-small text-dim">Use the email address your invitation was sent to.</p>
          </div>
          {(error || localError) && <Notice tone="danger">{error ?? localError}</Notice>}
          <Button onClick={() => void signInWithGoogle()} arrow={false} icon={<GoogleMark />}>
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-[0.6875rem] uppercase tracking-eyebrow text-dim">
            <span className="h-px flex-1 bg-gray-200" /> or <span className="h-px flex-1 bg-gray-200" />
          </div>
          {sent ? (
            <Notice tone="success" title="Check your inbox">
              We sent a sign-in link to {email}. Open it on this device to continue.
            </Notice>
          ) : (
            <form onSubmit={onEmail} className="grid gap-5">
              <Field label="Email">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required placeholder="you@company.com" />
              </Field>
              <Button type="submit" variant="ghost" arrow={false} disabled={busy || !email}>
                {busy ? 'Sending…' : 'Email me a sign-in link'}
              </Button>
            </form>
          )}
        </div>
      </section>
    </main>
  )
}

function GoogleMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.4Z" />
      <path fill="currentColor" opacity=".8" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" />
      <path fill="currentColor" opacity=".6" d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9l3.3-2.5Z" />
      <path fill="currentColor" opacity=".9" d="M12 6c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6 12 6Z" />
    </svg>
  )
}
